import React, { useRef, useState } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import { Container } from "@components/Container/Container";
import styles from "./styles.module.scss";
import stylesHeading from "@components/Heading/styles.module.scss";
import { ImageField } from "@helpers/interfaces/image";
import { ArticleBox } from "@components/ArticleBox/ArticleBox";
import Anime from "react-anime";
import { animeChildrenProps } from "@helpers/animations";
import { useInView } from "react-intersection-observer";
import { Heading } from "@components/Heading/Heading";
import { Box } from "@components/Box/Box";
import { UmbracoVideo } from "@components/UmbracoVideo/UmbracoVideo";
import { VideoField } from "@helpers/interfaces/media";
import { AspectRatio } from "@components/AspectRatio/AspectRatio";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import { Icon } from "@components/Icon/Icon";
import chevronLeft from "@svg-icons/chevron-left.component.svg";
import chevronRight from "@svg-icons/chevron-right.component.svg";
import classnames from "classnames";

interface mediaProps {
  Key: string;
  Title: string;
  TeaserText: string;
  Description: string;
  Image?: ImageField;
  Media?: {
    Video: VideoField;
  }[];
  VideoFormat?: string;
}

export interface MultipleMediaProps {
  headline: string;
  medias: mediaProps[];
  link: {
    Name?: string;
    Url?: string;
  };
}

const MultipleMedia: React.FC<MultipleMediaProps> = ({
  headline,
  medias,
  ...props
}) => {
  const { ref, inView } = useInView({
    threshold: 0.2
  });
  const swiperRef = useRef<SwiperCore>();
  const initSwiper = (swiper: SwiperCore) => {
    swiperRef.current = swiper;
  };

  const mediasModified = medias.filter(e => e.Media?.length || e.Image);

  const [selectedMedia, setSelectedMedia] = useState<mediaProps>(medias[0]);

  const selectMedia = (media: mediaProps, index: number) => {
    if (media.Media?.length === 0) {
      media.Media = undefined;
    }
    setSelectedMedia(media);
    swiperRef.current?.slideTo(index);
  };


  return (
    <div {...props}>
      <Container>
        <div ref={ref} style={{position: "relative" }}>
          {headline && (
            <Heading as="h2" className={stylesHeading.headingRibbon}>
              {headline}
            </Heading>
          )}
          <Box className={styles.swiperButtonsContainer}>
            <div className={classnames(styles.swiperButton, "image-swiper", "image-swiper-button-prev")}>
            <Icon svg={chevronLeft}/> 
            </div>
            <Box className={styles.content}></Box>
            <div className={classnames(styles.swiperButton, "image-swiper", "image-swiper-button-next")}>
              <Icon svg={chevronRight}/>
            </div>
          </Box>
          <Swiper
            slidesPerView="auto"
            className={styles.swiperContainer}
            onSwiper={swiper => initSwiper(swiper)}
            navigation={{
              nextEl: ".image-swiper-button-next",
              prevEl: ".image-swiper-button-prev",
              disabledClass: styles.swiperButtonDisabled
            }}
          >
            {mediasModified &&
              mediasModified.map((media: mediaProps, index: number) => {
                const animateEachChild = {
                  ...animeChildrenProps,
                  ...{
                    delay: (index * 100) / 1.5
                  },
                  ...(inView
                    ? { autoplay: true }
                    : {
                        autoplay: false
                      })
                };

                let image = media.Image as ImageField | string;
                if (media.Media && !media.Image) {
                  const mediaSelect = media.Media?.find((_e, i) => i === 0);
                  if (mediaSelect) {
                    image = mediaSelect.Video.thumbnail_url;
                  }
                }

                return (
                  <SwiperSlide
                    key={`${media.Key}_slide`}
                    className={styles.swiperSlideLarge}
                  >
                    <Anime {...animateEachChild}>
                      <button
                        onClick={() => selectMedia(media, index)}
                        className={styles.button}
                      >
                        <ArticleBox
                          image={image}
                          imageWidth={[500, 260, 300, 310]}
                          imageHeight={[281, 146, 168, 174]}
                          section={media.Title}
                          title={media.TeaserText}
                          showPlayIcon={
                            !media.Image && media.Media?.length === 1
                          }
                          sectionVariant="medium"
                          fontSize="small"
                          ratio="4:3"
                        />
                      </button>
                    </Anime>
                  </SwiperSlide>
                );
              })}
          </Swiper>
        </div>
        <Box
          className={styles.mediaContainer}
          key={`${selectedMedia.Key}_ImageVideo`}
        >
          {selectedMedia?.Image ? (
            <Box className={styles.mediaContainerVideo}>
              {selectedMedia?.VideoFormat ===
              "Brug Billedets Originale Format" ? (
                <UmbracoImage
                  image={selectedMedia.Image}
                  width={[350, 750, 800]}
                  isCover
                />
              ) : (
                <AspectRatio
                  ratio={
                    selectedMedia?.VideoFormat === "Brug 4:3 Format"
                      ? "4:3"
                      : "16:9"
                  }
                >
                  <UmbracoImage image={selectedMedia.Image} />
                </AspectRatio>
              )}
            </Box>
          ) : (
            <>
              {selectedMedia?.Media && selectedMedia.Media[0]?.Video && (
                <Box className={styles.mediaContainerVideo}>
                  <AspectRatio
                    ratio={
                      selectedMedia?.VideoFormat === "Brug 4:3 Format"
                        ? "4:3"
                        : "16:9"
                    }
                  >
                    {selectedMedia.Media[0].Video && (
                      <UmbracoVideo video={selectedMedia.Media[0].Video} />
                    )}
                  </AspectRatio>
                </Box>
              )}
            </>
          )}

          <Box className={styles.mediaContainerText}>
            {selectedMedia?.Title && (
              <Heading as="h2">{selectedMedia?.Title}</Heading>
            )}
            {selectedMedia?.Description && (
              <Box
                dangerouslySetInnerHTML={{ __html: selectedMedia?.Description }}
              />
            )}
          </Box>
        </Box>
      </Container>
    </div>
  );
};
export { MultipleMedia };
